import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { useToast } from '../../../components/ui/Toast';
import { platformConsumerService } from '../../../services/apiService';
import { parseApiError } from '../apiErrors';
import { PageHeader, StatusBadge, ErrorBanner, EmptyState } from '../_components/common';
import ConsumerFormModal from './ConsumerFormModal';

const Th = ({ children, className = '' }) => <th className={`text-left font-semibold px-4 py-3 ${className}`}>{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;

const PlatformConsumers = () => {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // { mode, consumer }
  const [confirm, setConfirm] = useState(null); // consumer pending suspend
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformConsumerService.list({ page: 1, limit: 100 });
      setData(res.data || []);
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (consumer, status) => {
    setActingId(consumer.id);
    try {
      await platformConsumerService.update(consumer.id, { status });
      toast.success(status === 'suspended' ? 'Consumer suspended' : 'Consumer reactivated');
      await load();
    } catch (err) {
      toast.error(parseApiError(err).message);
    } finally {
      setActingId(null);
      setConfirm(null);
    }
  };

  return (
    <div className="max-w-6xl">
      <PageHeader
        title="API Consumers"
        subtitle="Software clients that can be granted scoped, metered read APIs. Suspending a consumer immediately blocks all of its API definitions."
        actions={<Button iconName="Plus" onClick={() => setModal({ mode: 'create' })}>New consumer</Button>}
      />
      <ErrorBanner error={error} onClose={() => setError(null)} />

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Icon name="Loader" className="animate-spin inline mr-2" size={18} />Loading…</div>
      ) : data.length === 0 ? (
        <EmptyState icon="Boxes" title="No consumers yet" text="Create your first API consumer to get started."
          action={<Button iconName="Plus" onClick={() => setModal({ mode: 'create' })}>New consumer</Button>} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200">
              <tr><Th>Name</Th><Th>Environment</Th><Th>Status</Th><Th>Owner</Th><Th>Allowed IPs</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50">
                  <Td>
                    <div className="font-medium text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-400 font-mono">{String(c.id).slice(0, 8)}…</div>
                  </Td>
                  <Td className="capitalize text-slate-600">{c.environment}</Td>
                  <Td><StatusBadge status={c.status} /></Td>
                  <Td className="text-slate-600">{c.ownerContact || <span className="text-slate-300">—</span>}</Td>
                  <Td>{c.allowedIps?.length ? `${c.allowedIps.length} range${c.allowedIps.length > 1 ? 's' : ''}` : <span className="text-slate-400">Any</span>}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" iconName="Pencil" onClick={() => setModal({ mode: 'edit', consumer: c })}>Edit</Button>
                    {c.status === 'active' ? (
                      <Button variant="ghost" size="sm" iconName="PauseCircle" loading={actingId === c.id} onClick={() => setConfirm(c)}>Suspend</Button>
                    ) : (
                      <Button variant="ghost" size="sm" iconName="PlayCircle" loading={actingId === c.id} onClick={() => setStatus(c, 'active')}>Reactivate</Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ConsumerFormModal
          mode={modal.mode}
          consumer={modal.consumer}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && setStatus(confirm, 'suspended')}
        title="Suspend consumer?"
        message={confirm ? `Suspending "${confirm.name}" immediately blocks every API definition it owns from serving requests. You can reactivate it later.` : ''}
        confirmText="Suspend"
        variant="warning"
        loading={actingId === confirm?.id}
      />
    </div>
  );
};

export default PlatformConsumers;
